import React, { useState, useEffect, useMemo, useRef } from 'react';
import './DeNovoSetupModal.css';

const shuffleArray = (array) => {
    const newArr = [...array];
    for (let i = newArr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
};

const DeNovoSetupModal = React.memo(({
    isVisible,
    onStart,
    onClose,
    subjectMap,
    userAccessLevel,
    allItemsMap,
    userGoals,
    rulesProgress,
    onCreateCramDeck
}) => {
    const [isCramMode, setIsCramMode] = useState(false);
    const [deckName, setDeckName] = useState('');
    const [includeNew, setIncludeNew] = useState(false);
    const [includeReview, setIncludeReview] = useState(true);
    const [hfrOnly, setHfrOnly] = useState(false);
    const [limit, setLimit] = useState(50);
    const [selectedSubjects, setSelectedSubjects] = useState(['all']);

    const modalRef = useRef(null);
    const isFree = userAccessLevel === 'free';
    const fastTrackSubjects = useMemo(() => new Set(userGoals?.denovo_track_subjects || []), [userGoals]);

    useEffect(() => {
        if (isVisible) {
            modalRef.current?.focus();
            if (isFree) setSelectedSubjects(['torts']);
            // Reset to default state when opening
            setIsCramMode(false);
            setDeckName('');
        }
    }, [isVisible, isFree]);

    const toggleSubject = (key) => {
        if (isFree && key !== 'torts') return;
        setSelectedSubjects(prev => {
            if (key === 'all') return prev.includes('all') ? [] : ['all'];
            let list = prev.includes('all') ? [] : [...prev];
            if (list.includes(key)) list = list.filter(k => k !== key);
            else list.push(key);
            const allKeys = Object.keys(subjectMap);
            if (list.length === 0 || (!isFree && list.length === allKeys.length)) return ['all'];
            return list;
        });
    };
    
    useEffect(() => {
        if (isCramMode) {
            const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const subjectLabel = selectedSubjects.includes('all') ? 'All Subjects' : selectedSubjects.map(s => subjectMap[s]).join(', ');
            setDeckName(`${subjectLabel} Cram - ${date}`);
        }
    }, [isCramMode, selectedSubjects, subjectMap]);

    const { queue, stats, blockedSubjects } = useMemo(() => {
        if (!allItemsMap || !rulesProgress) return { queue: [], stats: { new: 0, review: 0, hfr: 0 }, blockedSubjects: [] };

        const activeSubjectKeys = selectedSubjects.includes('all') ? Object.keys(subjectMap) : selectedSubjects;
        const blockedSubs = new Set();
        let newRules = [], reviewRules = [], hfrCount = 0;

        allItemsMap.forEach(item => {
            if (item.itemType !== 'rule' || !activeSubjectKeys.includes(item.subject)) return;
            if (hfrOnly && !item.isHFR) return;

            const progress = rulesProgress[item.id];
            const isNew = !progress?.srs_data?.lastReviewedDate;

            if (isNew) {
                if (includeNew) {
                    if (fastTrackSubjects.has(item.subject)) newRules.push(item);
                    else blockedSubs.add(subjectMap[item.subject]);
                }
            } else if (includeReview) {
                reviewRules.push(item);
            }
        });

        newRules.sort((a, b) => (a.HNU || '999').localeCompare(b.HNU || '999', undefined, { numeric: true }));
        reviewRules = shuffleArray(reviewRules);

        const combined = [...newRules, ...reviewRules];
        combined.forEach(r => { if (r.isHFR) hfrCount++; });
        const finalQueueIds = combined.slice(0, limit).map(r => r.id);

        return {
            queue: finalQueueIds,
            stats: { new: Math.min(finalQueueIds.length, newRules.length), review: Math.max(0, finalQueueIds.length - newRules.length), hfr: hfrCount },
            blockedSubjects: Array.from(blockedSubs)
        };
    }, [allItemsMap, rulesProgress, selectedSubjects, subjectMap, hfrOnly, includeNew, includeReview, fastTrackSubjects, limit]);

    const handleStart = () => {
        if (queue.length === 0) return;
        if (isCramMode) {
            onCreateCramDeck(deckName, queue);
        } else {
            onStart({ queue });
        }
        onClose();
    };

    if (!isVisible) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="denovo-setup-modal theme-aware" onClick={e => e.stopPropagation()} ref={modalRef} tabIndex="-1">
                <header className="modal-header-pane">
                    <h2>Custom Session</h2>
                    <button className="icon-btn close-btn" onClick={onClose}><span className="material-symbols-outlined">close</span></button>
                </header>
                <div className="modal-scroll-content">

                  <div className="setup-section cram-mode-section">
                      <h4 className="section-label">Session Type</h4>
                        <div className="cram-toggle-wrapper">
                          <p><strong>Standard Session</strong></p>
                            <label className="toggle-switch large-toggle">
                                <input type="checkbox" checked={isCramMode} onChange={() => setIsCramMode(!isCramMode)} />
                                <span className="slider round"></span>
                            </label>
                            <p><strong>Cram Deck</strong></p>
                          
                          </div>
                          <div className="mode-info">
                            <p>Cram session progress is saved separately and will not affect your main spaced repetition schedule.</p>
                          </div>

                        {isCramMode && (
                            <div className="deck-name-input">
                                <label>Deck Name</label>
                                <input type="text" value={deckName} onChange={(e) => setDeckName(e.target.value)} />
                            </div>
                        )}
                    </div>

                    <div className="setup-section">
                        <h4 className="section-label">Session Composition</h4>
                        <div className="mode-selector-grid">
                            <div className={`mode-card ${includeReview ? 'active' : ''}`} onClick={() => setIncludeReview(!includeReview)}>
                                <span className="material-symbols-outlined mode-icon">update</span>
                                <div className="mode-info"><strong>Review Past Cards</strong><p>Reinforce rules you've started. Ignores dates.</p></div>
                                {includeReview && <span className="material-symbols-outlined check-icon">check_circle</span>}
                            </div>
                            <div className={`mode-card ${includeNew ? 'active' : ''}`} onClick={() => setIncludeNew(!includeNew)}>
                                <span className="material-symbols-outlined mode-icon">fiber_new</span>
                                <div className="mode-info"><strong>Learn New Rules</strong><p>Preview new cards via De Novo.</p></div>
                                {includeNew && <span className="material-symbols-outlined check-icon">check_circle</span>}
                            </div>
                        </div>
                        {includeNew && blockedSubjects.length > 0 && (
                            <div className="setup-warning-banner">
                                <span className="material-symbols-outlined">warning</span>
                                <div className="warning-text">
                                    <strong>Fast Track Restriction:</strong> New rules excluded for: <em>{blockedSubjects.slice(0, 3).join(', ')}{blockedSubjects.length > 3 && '...'}</em> because they are set to Linear Mode.
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="setup-section split-section">
                        <div className="filter-group">
                            <h4 className="section-label">Filters</h4>
                            <button className={`filter-toggle-pill ${hfrOnly ? 'active' : ''}`} onClick={() => setHfrOnly(!hfrOnly)}>
                                {hfrOnly ? 'HFR Only (Active)' : 'Include All Rules'}
                            </button>
                        </div>
                        <div className="limit-group">
                            <div className="limit-header"><h4 className="section-label" style={{ margin: 0 }}>Card Limit: {limit}</h4></div>
                            <input type="range" className="styled-slider" min="5" max="150" step="5" value={limit} onChange={e => setLimit(Number(e.target.value))} />
                        </div>
                    </div>
                    <div className="setup-section">
                        <h4 className="section-label">Subjects</h4>
                        <div className="subject-full-row">
                            <button className={`denovo-subject-chip full-width ${selectedSubjects.includes('all') ? 'active' : ''} ${isFree ? 'disabled' : ''}`} onClick={() => toggleSubject('all')}>All Subjects</button>
                        </div>
                        <div className="subject-grid">
                            {Object.entries(subjectMap).map(([k, v]) => (
                                <button key={k} className={`denovo-subject-chip ${selectedSubjects.includes(k) ? 'active' : ''} ${isFree && k !== 'torts' ? 'disabled' : ''}`} onClick={() => toggleSubject(k)}>{v} {isFree && k !== 'torts' && '🔒'}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <footer className="modal-footer-smart">
                    <div className="footer-stats">
                        <div className="stat-col"><span className="val">{stats.new}</span><span className="lbl">New</span></div>
                        <div className="stat-col"><span className="val">{stats.review}</span><span className="lbl">Review</span></div>
                        <div className="stat-col hfr-col"><span className="val">{stats.hfr}</span><span className="lbl">HFR</span></div>
                    </div>
                    <div className="footer-actions">
                        <button className="btn secondary" onClick={onClose}>Cancel</button>
                        <button className="btn primary" onClick={handleStart} disabled={queue.length === 0}>
                            {isCramMode ? `Create Deck (${queue.length})` : `Start Session (${queue.length})`}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
});

export default DeNovoSetupModal;